/**
 * Styles for the checkout modal.
 *
 * Everything here is applied *inside* the modal's closed shadow root, so the
 * selectors need no `#borga-checkout-modal-root` prefix — the shadow boundary
 * is the scope. It also means merchant page CSS (a Tailwind preflight, a
 * heavy-handed reset) cannot reach these nodes. Except the slotted iframe; see
 * `::slotted(iframe)` below.
 *
 * Inheritable properties (font, color, line-height, direction, …) *do* cross
 * the shadow boundary, which is what `:host { all: initial }` is for.
 */
export const MODAL_CSS = `
:host {
  all: initial;
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: block;
}

.overlay {
  position: absolute;
  inset: 0;
  display: flex; align-items: center; justify-content: center;
  box-sizing: border-box;
  padding: 24px;
  overflow-y: auto;
  /* Don't let a flick on the backdrop scroll-chain into the merchant page. */
  overscroll-behavior: contain;
  background: rgba(15, 23, 42, 0.55);
  animation: borga-fade 150ms ease-out;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/*
 * The panel never exceeds the overlay's content box: \`100%\` resolves against
 * the host, which modal.ts keeps the size of the *visual* viewport, so when a
 * phone keyboard takes the lower half of the screen the panel shrinks to the
 * part that is still visible. It's a column flex container so the frame
 * below shrinks with it (\`flex-shrink: 1; min-height: 0\`).
 */
.panel {
  position: relative;
  display: flex; flex-direction: column;
  width: 100%; max-width: 704px;
  max-height: 100%;
  background: #fff; color: #0f172a;
  border-radius: 10px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.24);
  animation: borga-slide 180ms ease-out;
}

/*
 * \`height\` is the frame's preferred size: the default when the hosted page
 * never reports its own content height (old checkout deployment, or a browser
 * without ResizeObserver), overridden by an inline height on \`borga:resize\`.
 * The panel's max-height clamps it either way — the frame shrinks and the
 * hosted page scrolls inside the iframe rather than the panel overflowing.
 */
.frame {
  position: relative;
  flex: 0 1 auto;
  min-height: 0;
  height: 760px;
  overflow: hidden;
  overscroll-behavior: contain;
  border-radius: inherit;
  background: #fff;
  opacity: 0;
  transition: opacity 120ms ease-out, height 160ms ease-out;
}
.frame.is-ready { opacity: 1; }

/* The iframe is light DOM (see modal.ts), so merchant CSS can reach it.
   Shadow-tree !important beats the page's, even its !important.
   Absolutely positioned rather than \`height: 100%\`: WebKit doesn't resolve a
   percentage height against a flex item whose size came from shrinking, so
   the iframe would stay as tall as its content and never scroll. */
::slotted(iframe) {
  position: absolute !important;
  inset: 0 !important;
  display: block !important;
  width: 100% !important;
  height: 100% !important;
  min-height: 0 !important;
  max-width: none !important;
  margin: 0 !important;
  border: 0 !important;
}

.loading {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: #fff;
  border-radius: inherit;
  transition: opacity 120ms ease-out, visibility 120ms ease-out;
}
.loading.is-hidden { opacity: 0; visibility: hidden; }

.spinner {
  width: 32px; height: 32px;
  box-sizing: border-box;
  border-radius: 999px;
  border: 3px solid rgba(148, 163, 184, 0.35);
  border-top-color: #64748b;
  animation: borga-spin 700ms linear infinite;
}

.close {
  position: absolute; top: -12px; right: -12px;
  z-index: 1;
  width: 32px; height: 32px;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid rgba(203, 213, 225, 0.9);
  background: #fff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.16);
  color: #64748b;
  border-radius: 999px; cursor: pointer;
  padding: 0;
}
.close svg { display: block; }
.close:hover {
  background: #f1f5f9; color: #0f172a;
}

@media (max-width: 760px) {
  .overlay {
    align-items: flex-start;
    padding: 20px 12px;
  }
  /* Fill the screen until the hosted page says how tall it really is. */
  .frame {
    height: 100vh;
  }
}

@media (prefers-reduced-motion: reduce) {
  .overlay, .panel, .spinner { animation: none; }
  .frame, .loading { transition: none; }
}

@keyframes borga-fade {
  from { opacity: 0; } to { opacity: 1; }
}
@keyframes borga-slide {
  from { transform: translateY(8px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
@keyframes borga-spin {
  to { transform: rotate(360deg); }
}
`;

/**
 * Attach {@link MODAL_CSS} to a shadow root.
 *
 * Preferred path is a constructed stylesheet via `adoptedStyleSheets`: it never
 * creates a `<style>` element, so merchants running a strict `style-src` CSP
 * (no `unsafe-inline`) still get a styled modal. A `<style>` element inside a
 * shadow root is *not* exempt from the page's CSP, so it is only the fallback
 * for engines without constructable stylesheets (Safari < 16.4).
 */
export function applyModalStyles(shadow: ShadowRoot): void {
  try {
    if (
      "adoptedStyleSheets" in shadow &&
      typeof CSSStyleSheet === "function" &&
      typeof CSSStyleSheet.prototype.replaceSync === "function"
    ) {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(MODAL_CSS);
      shadow.adoptedStyleSheets = [sheet];
      return;
    }
  } catch {
    // Constructor rejected (older engines) — fall through to <style>.
  }

  const style = document.createElement("style");
  style.textContent = MODAL_CSS;
  shadow.appendChild(style);
}
